import type { StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import moment from "moment";
import React, { useCallback, useMemo } from "react";
import type StripeTypes from "stripe";
import styled, { css } from "styled-components";
import type { RecurringPlanDefinition } from "common/stripeProducts";
import Analytics from "js/analytics";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import SalesFormDialog from "js/react/components/Dialogs/SalesFormDialog";

import type { MinimalBillingAddress } from "../../components/AddressForm/AddressForm";
import { LoadingState } from "../components/LoadingState";
import { app } from "js/namespaces";
import { InviteOnlyContent } from "./components/InviteOnlyContent";
import { isPPTAddin } from "js/config";
import sanitizeRelativeUrl from "common/utils/sanitizeRelativeUrl";
import { useScreenSize } from "js/react/hooks/useScreenSize";

import {
    getTeamCheckoutLabels,
    type TeamCheckoutFlow,
    type TeamCheckoutLabelOptions,
    TeamCheckoutSteps
} from "./TeamCheckoutFlow";
import FlowProgress from "./components/FlowProgress/FlowProgress";
import { TeamCheckoutDesktopLayout } from "./components/TeamCheckoutDesktopLayout";
import { TeamCheckoutMobileLayout } from "./components/TeamCheckoutMobileLayout";
import { t } from "js/i18n";

const FlowProgressContainer = styled.div`
    width: 100%;
    flex-shrink: 0;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    background: white;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    z-index: 3000;
`;

const CheckoutRoot = styled.div<{ $isInviteOnly: boolean; $useMobileLayout: boolean; $reactivateFlow: boolean }>`
    position: relative;
    width: 100%;
    height: ${props => (props.$isInviteOnly ? "auto" : props.$reactivateFlow ? "60vh" : "90vh")};
    padding: 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    ${props =>
        props.$useMobileLayout &&
        css`
            padding: 0;
            height: 100vh;
            height: ${props => (props.$reactivateFlow ? "80dvh" : "100dvh")};
        `}
`;

/**
 * Renders team checkout for three flows (see TeamCheckoutFlow.TeamCheckoutFlow):
 * 1. Reactivate – canceled subscription; seats locked, no trial, contact support in footer.
 * 2. New with trial – new workspace, eligible for trial; due today $0, trial end date, "Start Free Trial".
 * 3. New no trial – new workspace, user has Pro/Team; "Add Team Workspace", "Confirm", charge now.
 * Labels (title, submitLabel, loadingState, subTitle) are derived inside from checkoutFlow and labelOptions.
 */
export interface TeamCheckoutRedesignedViewProps {
    checkoutFlow: TeamCheckoutFlow;
    /** Optional label overrides (e.g. workspaceName for reactivate flow title). */
    labelOptions?: TeamCheckoutLabelOptions;
    error: string | null;
    teamCheckoutStep: TeamCheckoutSteps;
    billingInterval: "year" | "month";
    prices: {
        year: RecurringPlanDefinition;
        month?: RecurringPlanDefinition;
    };
    promo: StripeTypes.PromotionCode | null;
    seatCount: number;
    availableTrialDays: number;
    billingAddress: MinimalBillingAddress;
    paymentMethodCreationError: string | null;
    isSubmitting: boolean;
    canSubmit: boolean;
    onBillingIntervalChange: (billingInterval: "year" | "month") => void;
    onSeatCountChange: (seatCount: number) => void;
    onPaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
    onBillingAddressChange: ({
        address,
        isAddressValid,
    }: { address: MinimalBillingAddress; isAddressValid: boolean }) => void;
    onVerifyPromoCode: (promo: StripeTypes.PromotionCode) => Promise<void>;
    onSubmit: () => void;
    workspaceId?: string;
    taxAmountPerSeat: number | null;
    onSuccess: () => void;
    onCancel: () => void;
}

const MAX_SEATS = 20;

export function TeamCheckoutRedesignedView({
    checkoutFlow,
    labelOptions,
    error,
    teamCheckoutStep,
    billingInterval,
    prices,
    promo,
    seatCount,
    availableTrialDays,
    billingAddress,
    paymentMethodCreationError,
    isSubmitting,
    canSubmit,
    onBillingIntervalChange,
    onSeatCountChange,
    onPaymentElementChange,
    onBillingAddressChange,
    onVerifyPromoCode,
    onSubmit,
    taxAmountPerSeat,
    workspaceId,
    onSuccess,
    onCancel,
}: TeamCheckoutRedesignedViewProps) {
    const { title, submitLabel, loadingState, subTitle } = useMemo(
        () => getTeamCheckoutLabels(checkoutFlow, t, labelOptions),
        [checkoutFlow, labelOptions, t]
    );

    const isAnnual = billingInterval === "year";
    const trialExpirationDate = moment().add(availableTrialDays, "days").format("MMMM DD, YYYY");
    const isOverSeatLimit = seatCount > MAX_SEATS;
    const canStartTrial = canSubmit && !isOverSeatLimit;

    const summary = useMemo(() => {
        const planDefinition = prices[billingInterval];
        let amount = planDefinition.amount / 100;
        let adjustedTaxPerSeat = (taxAmountPerSeat ?? 0) / 100;
        if (promo?.coupon) {
            const amountBefore = amount;
            if (promo.coupon.amount_off) {
                amount = Math.max(amount - promo.coupon.amount_off / 100, 0);
            } else {
                amount = amount - (amount / 100) * promo.coupon.percent_off;
            }
            const ratio = amountBefore > 0 ? amount / amountBefore : 1;
            adjustedTaxPerSeat = adjustedTaxPerSeat * ratio;
        }
        const subtotal = amount * seatCount;
        const taxTotal = adjustedTaxPerSeat * seatCount;
        const dueNext = subtotal + taxTotal;
        return { pricePerSeat: amount, subtotal, taxTotal, dueNext };
    }, [prices, billingInterval, promo, seatCount, taxAmountPerSeat]);

    const handleContactSales = useCallback(() => {
        Analytics.ModifyWorkspacePlan.EnterpriseInquiry();
        ShowDialog(SalesFormDialog, {
            onClose: () => {
                onSeatCountChange(MAX_SEATS);
            },
        });
    }, [onSeatCountChange]);

    const { pricePerSeat, subtotal, taxTotal, dueNext } = summary;
    const annualCostAtMonthlyRate = ((prices.month?.amount ?? 0) * 12) / 100;
    const annualCostAtAnnualRate = prices.year.amount / 100;
    const annualSavingsPercentage =
        annualCostAtMonthlyRate > 0 ? (1 - annualCostAtAnnualRate / annualCostAtMonthlyRate) * 100 : 0;

    const { isMobile, width: windowWidth } = useScreenSize();
    const useMobileLayout = isMobile || windowWidth < 916;

    const checkoutLayoutProps = {
        checkoutFlow,
        title,
        subTitle,
        buttonLabel: submitLabel,
        error,
        seatCount,
        pricePerSeat,
        subtotal,
        taxTotal,
        dueNext,
        isAnnual,
        availableTrialDays,
        trialExpirationDate,
        annualSavingsPercentage,
        promo,
        billingAddress,
        paymentMethodCreationError,
        prices,
        onPaymentElementChange,
        onBillingAddressChange,
        onSeatCountChange,
        onContactSales: handleContactSales,
        onVerifyPromoCode,
        onBillingIntervalChange,
        onSubmit,
        canStartTrial,
        onCancel,
    };

    return (
        <CheckoutRoot
            $reactivateFlow={checkoutFlow === "reactivate"}
            $isInviteOnly={teamCheckoutStep === TeamCheckoutSteps.INVITE_USERS}
            $useMobileLayout={useMobileLayout}
        >
            {isSubmitting && (
                <LoadingOverlay>
                    <LoadingState title={loadingState} subtitle={t("This will only take a moment...")} />
                </LoadingOverlay>
            )}

            {teamCheckoutStep === TeamCheckoutSteps.INVITE_USERS && workspaceId && (
                <InviteOnlyContent
                    seatCount={seatCount ?? 0}
                    workspaceId={workspaceId}
                    onSuccess={() => {
                        if (isPPTAddin) {
                            onSuccess?.();
                            return;
                        }
                        const continueUrl = sanitizeRelativeUrl(
                            new URLSearchParams(window.location.search).get("continue") ?? "/"
                        );
                        if (continueUrl !== window.location.pathname) {
                            window.location.href = continueUrl;
                        } else {
                            window.location.reload();
                        }
                    }}
                />
            )}

            {teamCheckoutStep === TeamCheckoutSteps.CHECKOUT_OVERLAY &&
                (useMobileLayout ? (
                    <TeamCheckoutMobileLayout {...checkoutLayoutProps} />
                ) : (
                    <TeamCheckoutDesktopLayout {...checkoutLayoutProps} />
                ))}

            {!app.isMobileOrTablet && checkoutFlow !== "reactivate" && (
                <FlowProgressContainer>
                    <FlowProgress step={teamCheckoutStep} steps={Object.values(TeamCheckoutSteps)} />
                </FlowProgressContainer>
            )}
        </CheckoutRoot>
    );
}
